import { useState, useEffect, useCallback, useRef } from "react";

// ─── GOOGLE FONTS ───
if (typeof document !== "undefined") {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Readex+Pro:wght@300;400;500;600;700&display=swap";
  document.head.appendChild(link);

  // Mobile CSS
  const style = document.createElement("style");
  style.textContent = `
    * { -webkit-tap-highlight-color: transparent; }
    html, body, #root { background: #0a0a0a !important; }
    html, body { overflow-x: hidden; }
    body {
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
    }
    @media (max-width: 600px) {
      * { max-width: 100% !important; box-sizing: border-box !important; }
      pre, code { white-space: pre-wrap !important; word-break: break-all !important; }
      /* Layout */
      .founder-main { padding: 12px 10px !important; }
      .founder-header { padding: 6px 10px !important; }
      .founder-nav { padding: 5px 8px !important; }

      /* Typography */
      .founder-title { font-size: 18px !important; }
      .founder-subtitle { font-size: 13px !important; }
      .founder-typed { font-size: 13px !important; line-height: 1.7 !important; max-height: 300px !important; }
      .founder-why { font-size: 13px !important; line-height: 1.7 !important; }
      .founder-def { font-size: 13px !important; line-height: 1.7 !important; }
      .founder-takeaway { font-size: 13px !important; line-height: 1.7 !important; }
      .founder-pitfall { font-size: 12px !important; }
      .founder-intro { padding: 10px 12px !important; font-size: 13px !important; line-height: 1.7 !important; }

      /* Buttons */
      .founder-btn { font-size: 13px !important; padding: 13px 16px !important; width: 100% !important; }
      .founder-option { font-size: 13px !important; padding: 12px 12px !important; width: 100% !important; }

      /* Boxes - make all fluid */
      .founder-pane-body { padding: 10px 10px !important; }
      .founder-metric-val { font-size: 18px !important; }
      .founder-dashboard { grid-template-columns: 1fr !important; }

      /* Prevent ALL overflow */
      * { max-width: 100% !important; word-break: break-word; }
      pre, code { white-space: pre-wrap !important; overflow-x: hidden !important; }

      /* Fix monospace bar charts overflowing */
      .founder-typed div { overflow-x: hidden !important; }
    }
  `;
  document.head.appendChild(style);
}

// ─── DESIGN TOKENS ───
const C = {
  bg: "#0a0a0a",
  panel: "#0c0c0c",
  primary: "#33ff00",
  secondary: "#ffb000",
  muted: "#1f521f",
  dim: "#1a7a1a",
  error: "#ff3333",
  border: "#1f521f",
  cyan: "#00e5ff",
};
const GLOW = (c = C.primary, r = 5) => `0 0 ${r}px ${c}88`;
const FONT = "'JetBrains Mono','Fira Code','Courier New',monospace";
const FONT_AR = "'Readex Pro','Segoe UI',Tahoma,Arial,sans-serif";

// ═══════════════════════════════════════════════════
// ─── القصة: 6 فصول ───
// ═══════════════════════════════════════════════════
const CHAPTERS = [
  // ───────────────── الفصل الأول: البداية ─────────────────
  {
    id: "countdown",
    act: "I",
    actTitle: "البداية",
    title: "العد التنازلي",
    subtitle: "اليوم الأول كمؤسس حصل على تمويل. الساعة بدأت العد التنازلي.",
    terminalIntro: [
      "أنت جالس في مقهى. جوالك يرن:",
      "'500,000 ريال وصلت للحساب.'",
      "تاخذ نفس عميق. صارت حقيقية.",
      "",
      "نص مليون ريال!",
      "بس كل ريال له غرض، وكل شهر يمر بدون",
      "إيرادات يعني الرصيد ينقص.",
      "",
      "قبل ما تصرف ريال واحد، لازم تجاوب",
      "على سؤال:",
      "",
      ">> كم شهر بيكفيني هالمبلغ؟ <<",
    ],
    narrative: [
      "> تشغيل founder_os v0.1 ...",
      "> تحميل رصيد_البنك ............ 500,000   [OK]",
      "> تحميل الفريق ................. مؤسس واحد  [OK]",
      "> تحميل المنتج ................. idea.txt   [OK]",
      "> تحميل الإيرادات .............. 0         [OK]",
      "",
      "الشهر الأول. تركت شغلك. أسست الشركة.",
      "الشهر الثاني. وظفت أول مهندس. ثم ثاني.",
      "بحلول الشهر الثالث، عندك مهندسين وأنت تتولى",
      "المنتج والتصميم بنفسك.",
      "",
      "مكتب مشترك صغير: 500/شهر. بنية سحابية: 1,200/شهر.",
      "رواتب المهندسين: 24,000/شهر. أدوات برمجية: 800/شهر.",
      "مصاريف متنوعة: 1,500/شهر. راتبك أنت: 0.",
      "تبقي التكاليف خفيفة.",
      "",
      "المصاريف الشهرية: 28,000. الإيرادات الشهرية: 0.",
      "رصيد بنكك راح يصغر كل شهر من هنا.",
      "",
      "هذا أول درس في كونك مؤسس:",
      "هدفك إدارة التمويل على المدى القصير،",
      "وتبني شركة مستدامة على المدى الطويل.",
      "معرفة وقتك بالضبط هي الخطوة الأولى.",
    ],
    lesson: {
      title: "معدل الحرق والمدة المتبقية للتمويل (Burn Rate & Runway)",
      why: "السبب الأول اللي تتعثر فيه الشركات الناشئة مو منتجات سيئة، مو منافسة — لكن المال اللي في الحساب انتهى بدون نتيجة. معرفة كم باقي تعطيك الوضوح لاتخاذ قرارات ذكية بكل ريال تصرفه.",
      metrics: [
        {
          name: "BURN RATE — معدل الحرق",
          def: "كم ريال يطلع من حسابك كل شهر فوق ما يدخل. لو تصرف 28K/شهر وما تكسب شيء، معدل حرقك الصافي 28K/شهر. لما تبدأ الإيرادات: الحرق = المصاريف ناقص الإيرادات.",
          formula: "net_burn = المصاريف_الشهرية - الإيرادات_الشهرية",
        },
        {
          name: "RUNWAY — المدة المتبقية للتمويل",
          def: "كم شهر يغطي المال المتوفر على معدل الحرق الشهري. هذا أهم رقم عندك كمؤسس. يحدد كل قرار توظيف، وكل صرف تسويق، وكل جدول زمني للتحول.",
          formula: "runway_months = الكاش_في_البنك ÷ معدل_الحرق_الشهري",
        },
      ],
      scenario: {
        intro: "المنتج أُطلق من 3 أشهر. الفريق تعين، المكتب تجهز. المصاريف الشهرية: 28K. الإيرادات: 0. رصيد البنك: 500,000.",
        question: "كم باقي لك؟",
        options: [
          { label: "9 أشهر", value: 9, correct: false, feedback: "تشاؤمك في الحساب زيادة (بس الحيطة مهمة). 500,000 ÷ 28,000 ≈ 18 شهر. بس فايدة كلامك: مصاريفك غالباً راح تكبر مع التوظيف، فالوقت الباقي الفعلي دايماً أقصر من الحساب الرياضي." },
          { label: "~18 شهر", value: 18, correct: true, feedback: "صح! 500,000 ÷ 28,000 ≈ 17.8 شهر، تقريباً 18. هذي المدة الزمنية الباقية لك للتخطيط. المؤسس الذكي يخطط كأن عندهم 14 شهر — الأمور دايماً تكلف أكثر وتاخذ وقت أطول. تبي تبقى معاك 4-6 أشهر runway لما تبدأ جولة التمويل القادمة." },
          { label: "24 شهر", value: 24, correct: false, feedback: "500K ÷ 28K ≈ 18 شهر، مو 24. يستاهل تكون دقيق — المبالغة في تقدير المدة تخليك تصرف بحرية في البداية وتلاقي نفسك في ضيقة بعدين." },
          { label: "ما يهم — نأخذ تمويل ثاني", value: 99, correct: false, feedback: "التفاؤل شيء زين، بس 29% من فشل الشركات الناشئة سببه نفاد السيولة. التمويل ياخذ 3-6 أشهر وما هو مضمون. المؤسسين الحكيمين دايماً يعرفون رقمهم. رقمك تقريباً 18 شهر — وهذي المعرفة قوة." },
        ],
      },
      viz: { type: "runway", cash: 500000, burn: 28000, months: 20 },
    },
    pitfalls: [
      { mistake: "تحسب إجمالي المصاريف الشهرية الحالية بدون ما تطرح التكاليف الفردية القادمة — المصاريف الفردية مثل تأثيث المكتب أو شراء معدات", fix: "جدول تدفق نقدي شهري بسيط يشمل المصاريف الفردية. الوقت المتبقي الحقيقي دايماً أقصر من الكاش ÷ الحرق." },
      { mistake: "تتناسى إن معدل الحرق يكبر مع كل موظف جديد — حرق الشهر الأول مو نفس حرق الشهر السادس", fix: "احسب معدل حرقك للـ 6 أشهر القادمة مع التوظيفات المخططة. استخدم المتوسط المتوقع، مو الرقم اليوم." },
      { mistake: "تفترض إنك تقدر تأخذ تمويل أي وقت — التمويل ياخذ 3-6 أشهر وما هو مضمون", fix: "ابدأ البحث عن التمويل التالي إذا عندك 6+ أشهر من التمويل. لو انتظرت للشهر الثالث، راح تتفاوض من موقع ضعف." },
    ],
    takeaway: "معرفة كم يكفي التمويل بالضبط هي الخطوة الأولى.",
  },
  {
    id: "contact",
    act: "I",
    actTitle: "البقاء",
    title: "أول تواصل",
    subtitle: "بنيته. هل العملاء يجون؟ هل راح يبقون؟",
    terminalIntro: [
            "الشهر الرابع. على وشك تضغط 'إطلاق.'",
      "أربعة أشهر من البناء. ليالي بدون نوم.",
      "نقاشات حامية على الميزات.",
      "",
      "المصمم يقول 'يحتاج تعديل' المهندس يقول",
      "'مليان مشاكل.' المستثمر يقول 'أبي أشوف نتائج بالشهر السادس.'",
      "",
      "أطلقته. نشرت في كل مكان. أرسلت إيميل لكل من",
      "تعرفه. ثم فتحت لوحة التحليلات وانتظرت.",
      "",
      "5,000 شخص في اليوم الأول.",
      "بحلول اليوم السابع، تقريباً كلهم راحوا.",
      "",
      ">> وش صار؟ وين راحوا؟",
      "وكيف أقيس وش صار غلط؟ <<",
          ],
    narrative: [
      "> مراقبة الاتصالات الواردة...",
      "> اليوم_01: 5,000 زائر .......... [ارتفاع]",
      "> اليوم_01: 800 تسجيل ........... [زين؟]",
      "> اليوم_01: 340 أكملوا الإعداد   [همم]",
      "> اليوم_07: 180 رجعوا ........... [قلق]",
      "",
      "خلنا نتمهل. 5,000 شخص زاروا.",
      "800 أعطوك إيميلهم. هذا 16% — رقم لا بأس فيه.",
      "",
      "بس 340 فعلياً استخدموا منتجك.",
      "يعني 460 شخص سجلوا بس ما وصلوا لنقطة استخدام المنتج.",
      "ما شافوا اللحظة اللي يبدع فيها منتجك.",
      "",
      "ومن الـ 340 اللي جربوه، بس 180 رجعوا",
      "بعد أسبوع. في مجال للتحسين هنا.",
      "",
      "الحصول على العملاء يشبه القُمع بضم القاف.",
      "الناس تنزل في كل مرحلة بشكل طبيعي.",
      "المهم إنك تفهم وين ولماذا.",
      "فكر فيها كذا:",
      "[[FUNNEL_MINI]]",
      "كل مرحلة تخسر ناس. هذا الإطار اسمه",
      "AARRR — مقاييس القراصنة (لأنه يشبه صوت قرصان في مسلسل كرتوني).",
      "اسم مضحك، بس قوي جداً.",
      "يقولك بالضبط أين قمعك يحتاج تعديل.",
    ],
    lesson: {
      title: "AARRR — مقاييس القراصنة",
      why: "القمع (بضم القاف) هو تصوّر لرحلة المستخدم — يدخل كثير من الأعلى ويخرج أقل في كل مرحلة. ما تقدر تحسن شيء ما تقيسه. AARRR يعطيك إطاراً لتعرف أين قمعك يحتاج اهتمام — بدونه تخمن، معاه تعرف بالضبط أي مرحلة تركز عليها أول.",
      metrics: [
        {
          name: "ACQUISITION — الاكتساب",
          def: "كيف الناس تعرف إنك موجود؟ زيارات الموقع، تحميلات التطبيق، نقرات الإعلانات. هذا أعلى قمعك — أوسع جزء. لو ما أحد يجي، ما في شيء آخر يهم.",
          formula: "acquisition = إجمالي_الزوار_الجدد_أو_التحميلات",
        },
        {
          name: "ACTIVATION — التفعيل",
          def: "هل المستخدمين الجدد تجربتهم الأولى كانت كويسة؟ لما الشخص يتحول من 'وش هذا؟' لـ 'آه، فاهم!' لـ Dropbox كانت حفظ أول ملف. لـ Twitter متابعة 30 شخص. وش لحظتك أنت؟",
          formula: "activation_rate = المستخدمون_اللي_وصلوا_لحظة_الآها ÷ إجمالي_التسجيلات",
        },
        {
          name: "RETENTION — الاحتفاظ",
          def: "هل المستخدمين يرجعون بعد اليوم الأول؟ هذا أهم مقياس في القمع. لو الناس تجرب منتجك وما ترجع، عندك مشكلة منتج — مو مشكلة تسويق. حلها قبل ما تصرف على النمو.",
          formula: "احتفاظ_الأسبوع_الأول = المستخدمون_النشطون_اليوم_7 ÷ المستخدمون_اليوم_0",
        },
        {
          name: "REVENUE — الإيرادات",
          def: "تقدر تحول الاستخدام لفلوس؟ في النهاية المستخدمين المجانيين لازم يتحولون لزبائن يدفعون.",
          formula: "conversion_rate = المستخدمون_الدافعون ÷ المستخدمون_النشطون",
        },
        {
          name: "REFERRAL — الإحالة",
          def: "هل المستخدمين يحبونه بالدرجة اللي يحكون عنه لناس ثانيين؟ هذا أفضل دليل أن العميل أعجبه منتجك. لو كل مستخدم يجيب أكثر من مستخدم جديد (K-factor > 1)، عندك نمو عالي وتكلفة اكتسابك تقترب من الصفر.",
          formula: "k_factor = متوسط_الدعوات_لكل_مستخدم × معدل_تحويل_الدعوة",
        },
      ],
      scenario: {
        intro: "نتائج يوم الإطلاق: 5,000 زائر ← 800 تسجيل ← 340 أكملوا الإعداد ← 180 نشطين بعد أسبوع ← 27 دفعوا في النهاية ← 12 أحالوا صديق. معدل التفعيل هو % المسجلين اللي أكملوا الإعداد.",
        question: "كم معدل التفعيل عندك؟",
        options: [
          { label: "16% (800/5000)", value: 16, correct: false, feedback: "هذا معدل التسجيل — التحويل من زائر لمسجل. هذا Acquisition، مو Activation. التفعيل = اللي صار عميل فعلي عندك / المسجلين = 340/800 = 42.5%." },
          { label: "42.5% (340/800)", value: 42.5, correct: true, feedback: "صح. 340 فعّلوا / 800 مسجل = 42.5%. متوسط الصناعة 20-40%، فأنت فوق المتوسط قليلاً. بس انظر له بهالطريقة: 460 شخص أعطوك إيميلهم وما جربوا منتجك صح. هذي 460 فرصة ضائعة. وش لو كان إعدادك أحسن؟" },
          { label: "52.9% (180/340)", value: 52.9, correct: false, feedback: "هذا في الحقيقة معدل الاحتفاظ للأسبوع الأول — من اللي فعّلوا، كم منهم رجع. مقياس مهم، بس سؤال مختلف. التفعيل = أكملوا الإعداد / المسجلين = 340/800 = 42.5%." },
          { label: "3.6% (180/5000)", value: 3.6, correct: false, feedback: "هذا تحويلك للقمع الكامل (زوار لمستخدمين محتفظ بهم). مفيد تعرفه، بس التفعيل تحديداً يقيس المسجلين ← أول تجربة حقيقية = 340/800 = 42.5%." },
        ],
      },
      viz: {
        type: "funnel",
        data: [
          { stage: "الزوار", value: 5000 },
          { stage: "التسجيل", value: 800 },
          { stage: "فعّلوا", value: 340 },
          { stage: "بقوا أسبوع", value: 180 },
          { stage: "دفعوا", value: 27 },
          { stage: "أحالوا", value: 12 },
        ],
      },
    },
    pitfalls: [
      { mistake: "تحتفل بأعداد التسجيل بدون ما تتابع التفعيل — 800 تسجيل ما تعني شيء لو بس 340 استخدموا المنتج فعلياً", fix: "حدد الهدف بوضوح (مثلاً: 'أكمل أول مشروع') وتابع نسبة المسجلين اللي يوصلوا لها." },
      { mistake: "تصرف على الاستحواذ (إعلانات، تسويق) قبل ما تحل التفعيل والاحتفاظ — أنت تنفخ في قربة مشقوقة", fix: "اشتغل على القمع من الأسفل لفوق: حل الاحتفاظ بالعملاء أول، ثم التفعيل، ثم صب فلوس على العملاء الجدد." },
      { mistake: "تعامل جميع مراحل AARRR كأنها بنفس الأهمية — مو صح. الاحتفاظ دايماً أعلى رافعة", fix: "لو احتفاظ الأسبوع الأول أقل من 20%، وقف كل شيء وحل المنتج. ما في شيء آخر يهم لين الناس ترجع." },
    ],
    takeaway: "ما تعد المستخدمين بس — قيس أين يختفوا. حل أكبر تسريبة في قمعك أول. التفعيل دايماً تقريباً المرحلة اللي فيها أقل استثمار.",
  },

  // ───────────────── الفصل الثاني: الكفاح ─────────────────
  {
    id: "bucket",
    act: "II",
    actTitle: "الكفاح",
    title: "الدلو المثقوب",
    subtitle: "تنمو. بس في الحقيقة لا. الأرقام ما تكذب.",
    terminalIntro: [
            "الشهر الثامن. في اجتماع الفريق الأسبوعي.",
      "التسويق يبلغ عن رقم قياسي: 2,000 تسجيل جديد.",
      "الفريق متحمس.",
      "",
      "ثم فتحت لوحة إجمالي المستخدمين النشطين:",
      "1,024. الشهر اللي فات؟ 1,018.",
      "",
      "ضفت 2,000 شخص وكسبت... 6؟",
      "الغرفة هدت.",
      "",
      "في الليل، جتك رسالة من المستثمر:",
      "",
      "'احتفظ بعملائك قبل ما تصرف ريال ثاني",
      "على أنك تجيب عملاء جدد.'",
      "",
      ">> كلامه صح. وهذا السبب. <<",
          ],
    narrative: [
      "> تنبيه: تغير مكتشف",
      "> تسجيلات_جديدة_شهر_8: 2,000",
      "> صافي_تغيير_المستخدمين: +6",
      "> تشغيل تحليل_التسرب ............ [تم]",
      "> معدل_التسرب_الشهري: 12.4% ....... [حرج]",
      "",
      "هذا اللي يصير وراء الأرقام:",
      "",
      "بدأت الشهر الثامن بـ 1,018 مستخدم نشط.",
      "خلال الشهر، 126 منهم ألغوا أو صاروا غير نشطين.",
      "معدل التسرب: 12.4%.",
      "",
      "ضفت 2,000 تسجيل جديد. بعد ضياع التفعيل،",
      "تقريباً 132 صاروا مستخدمين نشطين حقيقيين.",
      "132 داخل، 126 خارج. صافي الربح: 6.",
      "",
      "تبذل مجهود، بس النتائج ما تظهر.",
      "كل ذلك النمو يُقابَل بمستخدمين راحلين.",
      "",
      "والأمر الحاسم: التسرب يتراكم.",
      "مو بس 12.4% من مستخدمي هذا الشهر.",
      "12.4% كل شهر، يتراكم على نفسه.",
      "فهم هذه الحسبة يغير أولوياتك.",
    ],
    lesson: {
      title: "الاحتفاظ والتسرب (Retention & Churn)",
      why: "التسرب هو التحدي الهادئ اللي يفاجئ كثير من المؤسسين. معدل تسرب شهري 5% يبدو صغير — بس يعني تخسر نص زبائنك كل سنة. ما في قدر من التسويق يقدر يتفوق على دلو مثقوب. الاحتفاظ هو الأساس اللي كل شيء آخر يُبنى عليه.",
      metrics: [
        {
          name: "CHURN RATE — معدل التسرب",
          def: "نسبة الزبائن اللي يتوقفون عن استخدام منتجك كل شهر. يبدو صغير — 'بس' 5% أو 10% — بس يتراكم بشكل كبير مع الوقت. بمعدل تسرب شهري 10%، تخسر 72% من زبائنك في سنة. بـ 5%، تخسر 46%. فهم هذا التراكم أساسي.",
          formula: "monthly_churn = العملاء_المفقودون_هذا_الشهر ÷ العملاء_في_بداية_الشهر",
        },
        {
          name: "NDR — صافي الاحتفاظ بالإيرادات",
          def: "الإيرادات من الزبائن الحاليين مقارنة بالفترة السابقة، شاملاً الترقيات والتخفيضات. لو NDR > 100%، زبائنك الحاليين يصرفون أكثر مع الوقت — تنمو حتى بدون زبائن جدد. أفضل شركات SaaS عندها NDR 120%+.",
          formula: "NDR = (MRR_البداية + التوسع - الانكماش - MRR_المتسرب) ÷ MRR_البداية",
        },
      ],
      scenario: {
        intro: "معدل تسربك 12.4% شهرياً. تبدأ بمجموعة 1,000 مستخدم اليوم. ما في مستخدمين جدد يُضافون — تتابع هذه المجموعة الأصلية. التسرب يتراكم: كل شهر تخسر 12.4% مما تبقى.",
        question: "بعد 12 شهر، كم من الـ 1,000 الأصليين لسا معاك؟",
        options: [
          { label: "~850 مستخدم تبقوا", value: 850, correct: false, feedback: "هذا بس شهر واحد من التسرب (1000 - 124 = 876). بس التسرب يتراكم. الشهر 2: 876 × 0.876 = 767. الشهر 3: 672. يفضل ينقص. بعد 12 شهر: 1000 × (0.876)^12 ≈ 196." },
          { label: "~500 مستخدم تبقوا", value: 500, correct: false, feedback: "في الحقيقة أقل من كذا. 12.4% تسرب شهري يتراكم بشكل كبير. 1000 × (1 - 0.124)^12 = 196. تخسر أكثر من 80% في سنة واحدة. هذا لماذا عضو مجلس الإدارة منركز على الاحتفاظ." },
          { label: "~196 مستخدم تبقوا", value: 196, correct: true, feedback: "صح. 1000 × (0.876)^12 ≈ 196. أكثر من 80% من مستخدميك راحوا في سنة واحدة. للمحافظة على عدد مستخدميك الحالي، تحتاج تكتسب 5 أضعاف قاعدتك كل سنة. هذا هذه مصيبة، مو نمو. الخبر الجيد: المحافظة على العملاء أثرها عالي جداً. حتى تخفيض التسرب من 12.4% لـ 5% يغير الصورة بشكل كبير." },
          { label: "~50 مستخدم تبقوا", value: 50, correct: false, feedback: "مو قاسية بهالدرجة — 1000 × (0.876)^12 ≈ 196. بس حدسك إنها مشكلة جدية صح. خسارة 80% من المستخدمين في سنة شيء يحتاج اهتمام فوري." },
        ],
      },
      viz: { type: "churn", startUsers: 1000, churnRate: 0.124, months: 12 },
    },
    pitfalls: [
      { mistake: "تعتقد إن 5% تسرب شهري 'صغير' — يتراكم لـ 46% خسارة سنوية. 10% شهرياً = 72% مشوا في سنة", fix: "دايماً حوّل تسربك لسنوي تحس بالأثر الحقيقي. التسرب الشهري × 12 مو الصيغة الصحيحة — استخدم (1 - churn)^12." },
      { mistake: "تقيس التسرب على إجمالي المستخدمين بدل المجموعات — يخفي إذا كانت الأمور تتحسن أو تتردى", fix: "تابع منحنيات الاحتفاظ بمجموعة العملاء — كل مجموعة حسب شهر أو أسبوع كسبهم كعملاء: كيف مجموعة يناير تتصرف مقارنة بمجموعة مارس؟ منحنيات تتحسن = تقدم حقيقي." },
      { mistake: "تخلط بين تسرب المستخدمين وتسرب الإيرادات — فقدان 10 مستخدمين مجانيين مختلف جداً عن فقدان 2 زبون enterprise", fix: "تابع تسرب العلامات (عملاء مفقودين) وتسرب الإيرادات (MRR مفقود) بشكل منفصل. تسرب الإيرادات هو اللي يضرب حسابك البنكي." },
    ],
    takeaway: "ركز على الاحتفاظ قبل الاستثمار في النمو. التسرب يتراكم — حتى التسرب الشهري 'الصغير' يآكل قاعدة مستخدميك أسرع مما تتوقع. أساس قوي من المستخدمين المحتفظ بهم يسهل كل شيء آخر.",
  },
  {
    id: "price",
    act: "II",
    actTitle: "الكفاح",
    title: "ثمن المجانية",
    subtitle: "المستخدمين يحبون منتجك. الحين وقت تبني شركة حوله.",
    terminalIntro: [
            "الشهر الثاني عشر. سويت الشركة. حليت مشكلة احتفاظ العملاء.",
      "التسرب نزل من 12.4% لـ 4.1%.",
      "مستخدميك يحبون المنتج. 2,400 مستخدم نشط.",
      "",
      "تتصل على شريكك تحتفلون.",
      "يقول: 'افتح الحساب.'",
      "",
      "164,000 متبقي. تقريباً 6 أشهر باقية.",
      "",
      "2,400 مستخدم. صفر إيرادات.",
      "بنيت شيء الناس تبيه.",
      "الحين تحتاج تبني شيء الناس تدفع",
      "مقابله بأسرع وقت.",
      "",
      ">> مستثمريك ما يهمهم 'الإيرادات.'",
      "يهمهم الإيرادات المتكررة.",
      "والفرق هذا مهم. <<",
          ],
    narrative: [
      "> pricing_tiers: مجاني | pro(19/شهر) | enterprise(79/شهر)",
      "> نشر paywall ............ [OK]",
      "> أول_دفعة_وصلت .......... [!!] 19.00",
      "",
      "أول ريال يدخل من عميل له وقع مختلف.",
      "شخص ما، في مكان ما، قرر إن منتجك يستاهل",
      "فلوس حقيقية. مو أخوك. مو صديقك. غريب.",
      "",
      "الشهر 12: 120 مستخدم ترقوا لـ Pro. هذا 2,280/شهر.",
      "الشهر 13: 180 Pro + 3 Enterprise = 3,657/شهر.",
      "الشهر 14: 240 Pro + 8 Enterprise = 5,192/شهر.",
      "",
      "بس لما مستثمرك يسأل 'كم إيراداتك؟'",
      "ما يقصد الـ 2,000 من استشارة.",
      "ما يقصد رسوم الإعداد الفردية.",
      "",
      "يقصد: أي فلوس تدخل كل شهر،",
      "بشكل متوقع، بدون ما تعيد البيع؟",
      "هذا MRR. وARR هو MRR مضروب في اثنا عشر.",
      "",
      "هذين الرقمين راح يحددون تقييم شركتك.",
    ],
    lesson: {
      title: "MRR وARR — الإيرادات المتكررة",
      why: "المستثمرين يقدرون القابلية للتنبؤ فوق كل شيء. شركة تكسب 100K/سنة من مشاريع فردية تساوي أقل بكثير من شركة تكسب 100K/سنة من اشتراكات. MRR وARR يقولان للمستثمرين: 'هذا الإيراد راح يكون هنا الشهر القادم، والشهر اللي بعده.' هو محرك تقييم SaaS.",
      metrics: [
        {
          name: "MRR — الإيرادات المتكررة الشهرية",
          def: "مجموع إيرادات الاشتراكات المتكررة والمتوقعة شهرياً. مو الدفعات الفردية. مو الخدمات. مو رسوم الإعداد. بس الفلوس اللي تجي تلقائياً، شهر بعد شهر، من الاشتراكات النشطة.",
          formula: "MRR = مجموع (السعر_الشهري × المشتركون_النشطون_في_كل_خطة)",
        },
        {
          name: "ARR — الإيرادات المتكررة السنوية",
          def: "MRR × 12. هذا الرقم اللي يحدد تقييمك. شركات SaaS المبكرة عادةً تُقيَّم بـ 10-20x ARR. فلو ARR عندك 62K، شركتك يمكن تساوي 620K - 1.2M على الورق.",
          formula: "ARR = MRR × 12",
        },
      ],
      scenario: {
        intro: "الشهر 14: عندك 240 مشترك Pro (19/شهر كل واحد) و8 زبائن Enterprise (79/شهر كل واحد). أيضاً أغلقت صفقة استشارية 2,000 فردية مع شركة أرادت مساعدة في التكامل.",
        question: "كم MRR عندك؟",
        options: [
          { label: "7,192 (كل الإيرادات هذا الشهر)", value: 7192, correct: false, feedback: "تضمّنت صفقة الاستشارة 2K. هذه إيرادات فردية — ما راح تتكرر الشهر القادم. MRR هو فقط المتكرر: (240 × 19) + (8 × 79) = 4,560 + 632 = 5,192." },
          { label: "5,192 (الاشتراكات بس)", value: 5192, correct: true, feedback: "صح. MRR = (240 × 19) + (8 × 79) = 4,560 + 632 = 5,192. صفقة الاستشارة 2K مستثناة — مو متكررة. ARR عندك = 5,192 × 12 = 62,304. بمضاعف 15x، هذا تقييم ~935K. بدأت تبني قيمة مؤسسية حقيقية." },
          { label: "4,560 (مشتركي Pro بس)", value: 4560, correct: false, feedback: "ما تنسى Enterprise! الـ 8 زبائن بـ 79/شهر يضيفون 632. MRR = 4,560 + 632 = 5,192. إيرادات Enterprise غالباً أسرع قطاع نمواً." },
          { label: "62,304 (سنوياً)", value: 62304, correct: false, feedback: "هذا ARR (MRR × 12)، مو MRR. فرق مهم: MRR = 5,192/شهر. ARR = 62,304/سنة. المستثمرين يستخدمون الاثنين، بس MRR هو اللي تتابعه شهرياً." },
        ],
      },
      viz: {
        type: "mrr",
        data: [
          { month: "ش12", pro: 2280, ent: 0 },
          { month: "ش13", pro: 3420, ent: 237 },
          { month: "ش14", pro: 4560, ent: 632 },
          { month: "ش15", pro: 5510, ent: 1027 },
          { month: "ش16", pro: 6460, ent: 1422 },
          { month: "ش17", pro: 7220, ent: 1896 },
        ],
      },
    },
    pitfalls: [
      { mistake: "تضمّن الدفعات الفردية (استشارة، رسوم إعداد، مشاريع تجريبية) في MRR — يضخم مقياسك المتكرر", fix: "MRR هو فقط إيرادات الاشتراك اللي تتكرر تلقائياً. الإيرادات الفردية تروح في سطر منفصل." },
      { mistake: "تحتسب العقود السنوية كـ ARR كامل مقدماً بدل توزيعها كـ MRR — عقد 12K سنوي = 1K MRR، مو 12K MRR", fix: "حوّل كل شيء لشهري: العقود السنوية ÷ 12 = إسهام MRR. ثم ARR = MRR × 12." },
      { mistake: "تتجاهل إن MRR ينقسم لـ 4 أجزاء: جديد (زبائن جدد)، ينمو (ترقيات)، ينقص (تخفيضات)، متسرب (إلغاءات) — الرقم الصافي يخفي القصة الحقيقية", fix: "تابع كل جزء منفصل كل شهر. MRR الصافي يرتفع بس المتسرب يرتفع أيضاً = إشارة تحذير مبكرة." },
    ],
    takeaway: "الدخل المستمر يبني الشركات.",
  },

  // ───────────────── الفصل الثاني (تكملة): فخ النمو ─────────────────
  {
    id: "trap",
    act: "II",
    actTitle: "الكفاح",
    title: "فخ النمو",
    subtitle: "اصرف لتكسب. بس كم الحد؟",
    terminalIntro: [
            "الشهر الثامن عشر. الإيرادات حقيقية. ARR",
      "تجاوز 100K. مستثمرك يتقهوى معك ويقول:",
      "'الحين نقدر نجيب عملاء جدد.",
      "خصص 8,000/شهر للتسويق.'",
      "",
      "وزعتها على 4 قنوات: Google Ads،",
      "تسويق المحتوى، مبيعات خارجية، إحالات.",
      "",
      "بعد شهرين، النتائج جت.",
      "بعض القنوات تشتغل كويس.",
      "قناة واحدة أداؤها ضعيف.",
      "",
      "تكتسب زبائن بـ 60 في قناة واحدة،",
      "وبـ 680 في ثانية. بس أي زبائن",
      "يستاهلون الصرف فعلاً؟",
      "",
      ">> تحتاج تعرف: هل كل زبون يساوي",
      "أكثر مما كلّف؟ هذي أهم معادلة",
      "في الشركات الناشئة. <<",
          ],
    narrative: [
      "> الميزانية: 8,000/شهر موزعة على 4 قنوات",
      "> تحليل تكلفة_الاكتساب ...... [تم]",
      "",
      ">  Channel           Spend   Customers  Cost/Cust",
      ">  ───────────────   ──────  ─────────  ─────────",
      ">  Organic/SEO       1,200   12         100",
      ">  Paid Search       3,400   5          680",
      ">  Content Mktg      2,000   8          250",
      ">  Referral          600     10         60",
      ">  Outbound          800     1          800",
      "",
      "الأرقام الخام واضحة: الإحالة الأرخص بـ 60",
      "للزبون. البحث المدفوع والخارجي غاليين.",
      "",
      "بس انتظر. الرخيص مو بالضرورة مربح.",
      "زبون كلفك 60 بس راح بعد شهرين",
      "ممكن يكون أسوأ من اللي كلّفك 680 وبقي 3 سنين.",
      "",
      "لهذا تحتاج رقمين، مو رقم واحد:",
      "كم يكلّف الحصول على زبون؟ (CAC)",
      "كم هي قيمة الزبون هذا؟ (LTV)",
      "والنسبة بينهم تقولك كل شيء.",
    ],
    lesson: {
      title: "CAC وLTV والنسبة الذهبية",
      why: "النمو بدون اقتصاديات الوحدة مجرد خسارة مكلفة. لو تصرف أكثر لكسب زبون من اللي راح يصرفه للشراء منك، كل زبون جديد يبعدك أكثر عن الاستدامة. نسبة LTV:CAC هي المعادلة اللي تفرق بين الشركات اللي تتوسع بربح والشركات اللي تتوسع نحو الهاوية.",
      metrics: [
        {
          name: "CAC — تكلفة اكتساب الزبون",
          def: "إجمالي صرف المبيعات والتسويق مقسوماً على عدد الزبائن الجدد المكتسبين في تلك الفترة. يخبرك 'سعر الشراء' لكل زبون جديد. تابعه لكل قناة — CAC المدمج يخفي القنوات اللي تشتغل واللي ما تشتغل.",
          formula: "CAC = إجمالي_صرف_المبيعات_والتسويق ÷ الزبائن_الجدد_المكتسبون",
        },
        {
          name: "LTV — قيمة عمر الزبون",
          def: "إجمالي الإيرادات اللي يدفعها زبون طول علاقته معك. يعتمد على ثلاثة أشياء: كم يدفع شهرياً (ARPU)، كم يبقى (1/churn)، وهامش ربحك. زبون يدفع 22/شهر يبقى 20 شهر بهامش 80% = LTV 352.",
          formula: "LTV = ARPU × متوسط_عمر_الزبون × هامش_الربح_الإجمالي",
        },
        {
          name: "نسبة LTV:CAC",
          def: "النسبة الذهبية لاقتصاديات الشركات الناشئة. أقل من 1:1 = تخسر على كل زبون. 3:1 = صحي (مقابل كل 1 تصرفه على الاكتساب، ترجع 3). فوق 5:1 = على الأرجح تقصّر في الاستثمار في النمو وتخسر فرص.",
          formula: "المستهدف: LTV/CAC ≥ 3.0  |  فترة الاسترداد < 12 شهر",
        },
      ],
      scenario: {
        intro: "الزبون يدفع متوسط 22/شهر، ويبقى معك تقريباً 20 شهر، وهامش ربحك 80%. بالحساب: LTV = 22 × 20 × 0.80 = 352. تكلفة اكتساب الزبون (CAC) عبر كل قنواتك: 220.",
        question: "نسبة LTV:CAC عندك 352 / 220 = 1.6:1. هل هذا كافي؟",
        options: [
          { label: "إيه — نربح على كل زبون", value: 1, correct: false, feedback: "أنت تقنياً مربح لكل زبون، بس 1.6:1 ضيقة جداً. بعد حساب المصاريف العامة، تكاليف الدعم، والقيمة الزمنية للمال، تقريباً ما في هامش متبقي. المعيار 3:1 — عندك فرصة تقريباً تضاعف هذه النسبة بتحسين قنواتك." },
          { label: "لا — نحتاج على الأقل 3:1 عشان يكون الوضع صحي", value: 2, correct: true, feedback: "صح تماماً. 1.6:1 يعني مقابل كل 1 تصرفه، ترجع 1.60 — بس هذا بالكاد يغطي المصاريف العامة وتكاليف الدعم. بـ 3:1، عندك هامش حقيقي لإعادة الاستثمار. روافعك: (1) اخفض CAC — ضاعف الإحالة (CAC 60) واقطع الخارجي (800). (2) ارفع LTV — قلل التسرب لتطويل العمر، أو رقّي الزبائن. (3) الاثنين معاً." },
          { label: "ارفع أسعارنا للضعف بس", value: 3, correct: false, feedback: "رفع الأسعار للضعف يرفع LTV لـ 704 والنسبة لـ 3.2:1 — ممتاز على الورق. بس ارتفاع الأسعار ممكن يرفع التسرب. رفع 2x بزيادة تسرب 25% يمكن يسوي الأمور أسوأ. أفضل تصلح النسبة بروافع متعددة: حسّن CAC وLTV مع بعض." },
          { label: "هذا زين للمرحلة المبكرة", value: 4, correct: false, feedback: "CAC المرتفع في المرحلة المبكرة مفهوم، بس 1.6:1 يترك مجال ضيق جداً للتقلب. لو التسرب ارتفع قليلاً أو CAC زاد بعض الشيء، ممكن تجد نفسك تحت 1:1. المؤسسين المتمرسين يعاملون 3:1 كأدنى نسبة صحية قبل توسيع الصرف." },
        ],
      },
      viz: {
        type: "ltvcac",
        channels: [
          { name: "عضوي/SEO", cac: 100, ltv: 420 },
          { name: "بحث مدفوع", cac: 680, ltv: 280 },
          { name: "تسويق محتوى", cac: 250, ltv: 380 },
          { name: "إحالة", cac: 60, ltv: 510 },
          { name: "خارجي", cac: 800, ltv: 340 },
        ],
      },
    },
    pitfalls: [
      { mistake: "تحسب LTV بإجمالي الإيرادات بدل صافي الربح الإجمالي — لو هامشك 60%، زبون 22/شهر LTV الحقيقي 13.20/شهر، مو 22", fix: "دايماً اضرب في هامش الربح الإجمالي: LTV = ARPU × lifespan × gross_margin. بدون الهامش، تبالغ في تقدير قيمة كل زبون." },
      { mistake: "تستخدم CAC المدمج عبر كل القنوات — يخفي إن الإحالة (60) والخارجي (800) مختلفين جداً", fix: "تابع CAC لكل قناة. ضاعف القنوات اللي LTV:CAC > 3:1. اقطع أو صلح القنوات تحت 1:1." },
      { mistake: "تتجاهل فترة الاسترداد — حتى لو LTV:CAC 3:1، لو ياخذ 24 شهر للاسترداد، تحتاج كاش كثير مقدماً للنمو", fix: "تابع كم شهر يلزم لاسترداد تكلفة اكتساب الزبون — تقسم CAC على صافي ما يدفعه الزبون شهرياً بعد حساب هامش الربح. أقل من 12 شهر صحي، فوق 18 شهر خطر على التدفق النقدي." },
    ],
    takeaway: "افهم قيمة كل زبون قبل توسيع الصرف. اهدف لـ LTV:CAC على الأقل 3:1. تابعه لكل قناة — رقمك المدمج يمكن يخفي أي قنوات تستحق استثمار أكثر وأيها تحتاج إعادة تفكير.",
  },

  // ───────────────── الفصل الثالث: المحطة الفاصلة ─────────────────
  {
    id: "board",
    act: "III",
    actTitle: "المحطة الفاصلة",
    title: "اجتماع مجلس الإدارة",
    subtitle: "الشهر 24. أرقامك تحكي قصة. اجعلها مقنعة.",
    terminalIntro: [
            "الشهر 24. تدخل غرفة الاجتماع.",
      "مستثمرك ينتظرك.",
      "",
      "في الـ 6 أشهر الأخيرة، اتخذت قرارات صعبة:",
      "- ألغيت البحث المدفوع كلياً (LTV:CAC 0.41:1)",
      "- ضاعفت ميزانية الإحالة (LTV:CAC 8.5:1)",
      "- أعدت بناء الإعداد (activation: 42% ← 67%)",
      "- حللت أسوأ محركات التسرب (12.4% ← 3.1%)",
      "",
      "مستثمرك ويقول:",
      "'هات الأرقام.'",
      "",
      "هذي اللحظة. كل مقياس تعلمته يقود لهنا.",
      "مو بس الأرقام — القصة اللي تحكيها.",
      "",
      ">> الشركة الناشئة الجيدة ليست جدول بيانات.",
      "هي قصة، مدعومة بالبيانات. <<",
          ],
    narrative: [
      "> تحميل بيانات_24_شهر .............. [OK]",
      "> توليد تقرير_المستثمرين ............. [OK]",
      "> مستوى_الثقة: عالي",
      "",
      "قبل 24 شهر، كان عندك 500K وفكرة.",
      "اليوم، عندك آلة.",
      "",
      "تعلمت إن RUNWAY هو أفق تخطيطك.",
      "تعلمت إن AARRR يكشف أين ينكسر القمع.",
      "تعلمت إن التسريب وخسارة العملاء يتراكم بصمت ضدك.",
      "تعلمت إن MRR هو المحرك الذي يقدّره المستثمرون.",
      "تعلمت إن LTV:CAC هي المعادلة اللي تثبت",
      "إن نموذج عملك يشتغل.",
      "",
      "كل رقم على هذه اللوحة فصل",
      "من قصة عشتها. والسؤال الحقيقي اللي",
      "مستثمرك يسأله مو 'كم مقاييسك؟'",
      "بل هذا:",
      "",
      "'لو استثمرت أكثر، هل هذه الآلة راح تتوسع؟'",
      "",
      "خلنا نشوف إجابتك.",
    ],
    lesson: {
      title: "الصورة الكاملة — مقاييسك، قصتك",
      why: "ما من مقياس يوجد بمعزل عن الآخر. ARR لا يعني شيئاً بدون معرفة تسربك. CAC منخفض لا يعني شيئاً لو LTV أقل منه. معدل تفعيل ممتاز لا يعني شيئاً لو ما أحد يجي. القوة في كيفية تواصل المقاييس — والقصة اللي تحكيها عن وجهة شركتك.",
      metrics: [],
      scenario: null,
      viz: {
        type: "dashboard",
        data: {
          arr: "240K",
          mrr: "20K",
          burn: "32K/شهر",
          runway: "14 شهر",
          ltv: "580",
          cac: "135",
          ratio: "4.3:1",
          churn: "3.1%",
          ndr: "112%",
          activation: "67%",
          customers: 680,
        },
      },
    },
    pitfalls: [
      { mistake: "تعرض المقاييس بدون ربطها — ARR وحده لا يثير إعجاباً لو التسرب يأكل النمو", fix: "احكِ القصة: 'تسربنا نزل من 12% لـ 3%، مما ضاعف LTV، مما رفع نسبة LTV:CAC من 1.6 لـ 4.3.'" },
      { mistake: "تانتقي أفضل مقاييسك وتخبي الضعيفة — المستثمرين راح يلاقوها، وتخسر مصداقيتك", fix: "ابدأ بنقاط قوتك، لكن تناول نقاط الضعف باستباقية: 'CAC لسا مرتفع في البحث المدفوع — هذي خطتنا لحله.'" },
      { mistake: "تحسّن المقاييس بمعزل بدل النظر لها كنظام — تخفيض CAC بتخفيض جودة الـ leads يسوء الاحتفاظ وLTV", fix: "دايماً تحقق من التأثيرات الثانوية. لما تغير مقياساً واحداً، اسأل: وش يحصل للثانيين؟ اللوحة نظام متصل." },
    ],
    takeaway: "المقاييس ما توجد بمعزل. القوة الحقيقية في القصة اللي يحكونها مع بعض: كيف تبقى (runway)، كيف تنمو (AARRR)، كيف تحتفظ (retention)، كيف تكسب (MRR)، وكيف تتوسع (LTV:CAC).",
  },
];


// ═══════════════════════════════════════════════════
// ─── المكونات المشتركة ───
// ═══════════════════════════════════════════════════

function Scanlines() {
  return <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, background: "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.06) 2px,rgba(0,0,0,0.06) 4px)" }} />;
}

function Cursor({ color = C.primary }) {
  const [on, setOn] = useState(true);
  useEffect(() => { const i = setInterval(() => setOn(v => !v), 530); return () => clearInterval(i); }, []);
  return <span style={{ color, opacity: on ? 1 : 0 }}>█</span>;
}

function TypedLines({ lines, speed = 25, lineDelay = 60, onDone, color = C.primary }) {
  const [displayed, setDisplayed] = useState([]);
  const [curLine, setCurLine] = useState(0);
  const [curChar, setCurChar] = useState(0);
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (curLine >= lines.length) { setDone(true); onDone?.(); return; }
    const line = lines[curLine];
    if (line === "" || line === "[[FUNNEL_MINI]]" || curChar >= line.length) {
      const t = setTimeout(() => { setDisplayed(d => [...d, line]); setCurLine(l => l + 1); setCurChar(0); }, line === "" ? lineDelay / 2 : lineDelay);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCurChar(c => c + 1), line.startsWith(">") ? 10 : speed);
    return () => clearTimeout(t);
  }, [curLine, curChar, lines, speed, lineDelay]);

  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [displayed, curChar]);

  const FUNNEL_DATA = [
    { label: "Visitors ", value: 5000, max: 5000 },
    { label: "Signups  ", value: 800,  max: 5000 },
    { label: "Activated", value: 340,  max: 5000 },
    { label: "Retained ", value: 180,  max: 5000 },
    { label: "Paid     ", value: 27,   max: 5000 },
    { label: "Referred ", value: 12,   max: 5000 },
  ];
  const FUNNEL_COLORS = [C.primary, "#2bcc00", C.secondary, "#e09500", "#ff7733", C.cyan];
  const BAR_W = typeof window !== "undefined" && window.innerWidth <= 600 ? 110 : 160;

  const isCmd = (l) => l.startsWith(">");
  const isMini = (l) => l === "[[FUNNEL_MINI]]";

  const renderLine = (l, i) => {
    if (isMini(l)) {
      return (
        <div key={i} style={{ margin: "6px 0" }}>
          {FUNNEL_DATA.map((d, j) => {
            const filled = Math.round((d.value / d.max) * BAR_W);
            const col = FUNNEL_COLORS[j];
            return (
              <div key={j} style={{ display: "flex", alignItems: "center", marginBottom: 3 }}>
                <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, width: 72, flexShrink: 0 }}>{d.label}</span>
                <div style={{ width: BAR_W, display: "flex", flexShrink: 0 }}>
                  <div style={{ width: filled, height: 10, background: col, boxShadow: GLOW(col, 2) }} />
                  <div style={{ width: BAR_W - filled, height: 10, background: `${col}22` }} />
                </div>
                <span style={{ fontFamily: FONT, fontSize: 11, color: col, width: 46, textAlign: "right", paddingLeft: 8, flexShrink: 0 }}>{d.value.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div key={i} style={{ color: isCmd(l) ? C.dim : color, textShadow: isCmd(l) ? GLOW(C.primary, 3) : "none", whiteSpace: "pre-wrap", minHeight: l === "" ? 10 : undefined }}>{l}</div>
    );
  };

  return (
    <div ref={ref} className="founder-typed" style={{ fontFamily: FONT_AR, fontSize: 15, lineHeight: 1.9, maxHeight: 400, overflowY: "auto", overflowX: "hidden", maxWidth: "100%" }}>
      {displayed.map((l, i) => renderLine(l, i))}
      {!done && curLine < lines.length && !isMini(lines[curLine]) && (
        <div style={{ color: isCmd(lines[curLine]) ? C.dim : color, textShadow: isCmd(lines[curLine]) ? GLOW(C.primary, 3) : "none" }}>
          {lines[curLine].slice(0, curChar)}<Cursor />
        </div>
      )}
    </div>
  );
}

function Pane({ title, children, accent = C.primary, status, style = {} }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.panel, maxWidth: "100%", overflow: "hidden", ...style }}>
      <div style={{ background: C.border, padding: "5px 12px", fontFamily: FONT_AR, fontSize: 12, color: accent, letterSpacing: 0, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
        <span>{title}</span>
        {status && <span style={{ color: status === "OK" || status === "LIVE" ? C.primary : status === "WARN" ? C.secondary : C.error, fontSize: 10 }}>[{status}]</span>}
      </div>
      <div className="founder-pane-body" style={{ padding: "14px 16px", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>{children}</div>
    </div>
  );
}

// ─── المخططات البيانية ───

function RunwayViz({ cash, burn, months }) {
  const data = []; let rem = cash;
  for (let i = 0; i <= months; i++) { data.push(rem); rem = Math.max(0, rem - burn); }
  return (
    <Pane title="محاكاة Runway" status="LIVE" accent={C.secondary}>
      <div style={{ overflowX: "hidden" }}>
      <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginBottom: 10, letterSpacing: 0 }}>{">"} {"الكاش المتبقي بالشهر (K)"} {"-"} {(burn/1000).toFixed(0)}{"K/شهر حرق"}</div>
      {data.map((v, i) => {
        const w = 22, filled = Math.round((v / cash) * w);
        const col = v > cash * 0.5 ? C.primary : v > cash * 0.2 ? C.secondary : C.error;
        return (
          <div key={i} style={{ fontFamily: FONT, fontSize: 11, display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
            <span style={{ color: C.muted, width: 28 }}>ش{String(i).padStart(2, "0")}</span>
            <span style={{ color: col, textShadow: GLOW(col, 2) }}>{"█".repeat(filled)}{"░".repeat(w - filled)}</span>
            <span style={{ color: col, width: 52, textAlign: "right" }}>{(v / 1000).toFixed(0)}K</span>
            {v === 0 && <span style={{ color: C.error, fontSize: 10, fontWeight: "bold" }}> لا يوجد مال</span>}
          </div>
        );
      })}
      </div>
    </Pane>
  );
}

function FunnelViz({ data }) {
  const max = data[0].value;
  const labels = ["Visitors ", "Signups  ", "Activated", "Retained ", "Paid     ", "Referred "];
  const BAR_W = typeof window !== "undefined" && window.innerWidth <= 600 ? 110 : 160;
  return (
    <Pane title="قمع AARRR — القراصنة" status="OK" accent={C.secondary}>
      <div style={{ fontFamily: FONT_AR, fontSize: 12, color: C.muted, marginBottom: 10 }}>{">"} يوم الإطلاق - قمع التحويل الكامل</div>
      {data.map((d, i) => {
        const filled = Math.round((d.value / max) * BAR_W);
        const colors = [C.primary, "#2bcc00", C.secondary, "#e09500", "#ff7733", C.cyan];
        const col = colors[i];
        const conv = i > 0 ? `${((d.value / data[i - 1].value) * 100).toFixed(1)}%` : null;
        return (
          <div key={i} style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, width: 72, flexShrink: 0 }}>{labels[i]}</span>
              <div style={{ width: BAR_W, flexShrink: 0, display: "flex", alignItems: "center" }}>
                <div style={{ width: filled, height: 10, background: col, boxShadow: GLOW(col, 2) }} />
                <div style={{ width: BAR_W - filled, height: 10, background: `${col}22` }} />
              </div>
              <span style={{ fontFamily: FONT, fontSize: 11, color: col, width: 46, textAlign: "right", flexShrink: 0, paddingLeft: 8 }}>{d.value.toLocaleString()}</span>
              {conv && <span style={{ fontFamily: FONT, fontSize: 10, color: C.muted, paddingLeft: 8 }}>← {conv}</span>}
            </div>
          </div>
        );
      })}
    </Pane>
  );
}

function ChurnViz({ startUsers, churnRate, months }) {
  const data = []; let u = startUsers;
  for (let i = 0; i <= months; i++) { data.push(Math.round(u)); u *= (1 - churnRate); }
  return (
    <Pane title="تآكل التسرب - 1,000 مستخدم أصليين" status="CRITICAL" accent={C.error}>
      <div style={{ fontFamily: FONT_AR, fontSize: 12, color: C.muted, marginBottom: 10 }}>{">"} تسرب متراكم @ {(churnRate * 100).toFixed(1)}{"% /شهر - بدون إضافة مستخدمين جدد"}</div>
      {data.map((v, i) => {
        const w = 24, filled = Math.round((v / startUsers) * w);
        const col = v > startUsers * 0.5 ? C.primary : v > startUsers * 0.25 ? C.secondary : C.error;
        return (
          <div key={i} style={{ fontFamily: FONT, fontSize: 11, display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
            <span style={{ color: C.muted, width: 28 }}>ش{String(i).padStart(2, "0")}</span>
            <span style={{ color: col, textShadow: GLOW(col, 2) }}>{"█".repeat(filled)}{"░".repeat(w - filled)}</span>
            <span style={{ color: col, width: 40, textAlign: "right" }}>{v}</span>
            {i === months && <span style={{ color: C.error, fontSize: 10 }}> ← {((1 - v / startUsers) * 100).toFixed(0)}% ضاعوا</span>}
          </div>
        );
      })}
    </Pane>
  );
}

function MrrViz({ data }) {
  const max = Math.max(...data.map(d => d.pro + d.ent));
  return (
    <Pane title="نمو MRR — مقسّم بالباقة" status="OK" accent={C.primary}>
      <div style={{ fontFamily: FONT_AR, fontSize: 12, color: C.muted, marginBottom: 10 }}>{">"} الإيرادات المتكررة الشهرية</div>
      {data.map((d, i) => {
        const w = 24, proW = Math.round((d.pro / max) * w), entW = Math.round((d.ent / max) * w), emptyW = Math.max(0, w - proW - entW);
        return (
          <div key={i} style={{ fontFamily: FONT, fontSize: 11, display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
            <span style={{ color: C.muted, width: 32 }}>{d.month}</span>
            <span><span style={{ color: C.primary, textShadow: GLOW(C.primary, 2) }}>{"█".repeat(proW)}</span><span style={{ color: C.secondary, textShadow: GLOW(C.secondary, 2) }}>{"█".repeat(entW)}</span><span style={{ color: C.muted }}>{"░".repeat(emptyW)}</span></span>
            <span style={{ color: C.primary, width: 60, textAlign: "right" }}>{((d.pro + d.ent) / 1000).toFixed(1)}K</span>
          </div>
        );
      })}
      <div style={{ fontFamily: FONT, fontSize: 10, color: C.muted, marginTop: 8, display: "flex", gap: 16 }}>
        <span><span style={{ color: C.primary }}>██</span> Pro 19/شهر</span>
        <span><span style={{ color: C.secondary }}>██</span> Enterprise 79/شهر</span>
      </div>
    </Pane>
  );
}

function LtvCacViz({ channels }) {
  const maxVal = Math.max(...channels.flatMap(c => [c.cac, c.ltv]));
  return (
    <Pane title="LTV مقابل CAC بالقناة" status="WARN" accent={C.secondary}>
      <div style={{ fontFamily: FONT_AR, fontSize: 12, color: C.muted, marginBottom: 10 }}>{">"} أخضر = قيمة العمر | أحمر = تكلفة الاكتساب | صحي ≥ 3:1</div>
      {channels.map((ch, i) => {
        const w = 16, ltvW = Math.round((ch.ltv / maxVal) * w), cacW = Math.round((ch.cac / maxVal) * w);
        const ratio = (ch.ltv / ch.cac).toFixed(1);
        const ok = ch.ltv / ch.cac >= 3, warn = ch.ltv / ch.cac >= 1;
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginBottom: 2 }}>{ch.name}</div>
            <div style={{ fontFamily: FONT, fontSize: 11, display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ width: 30, color: C.muted, fontSize: 10 }}>LTV</span>
              <span style={{ color: C.primary, textShadow: GLOW(C.primary, 2) }}>{"█".repeat(ltvW)}{"░".repeat(w - ltvW)}</span>
              <span style={{ color: C.primary, width: 50 }}>{ch.ltv.toLocaleString()}</span>
            </div>
            <div style={{ fontFamily: FONT, fontSize: 11, display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ width: 30, color: C.muted, fontSize: 10 }}>CAC</span>
              <span style={{ color: C.error, textShadow: GLOW(C.error, 2) }}>{"█".repeat(cacW)}{"░".repeat(w - cacW)}</span>
              <span style={{ color: C.error, width: 50 }}>{ch.cac.toLocaleString()}</span>
              <span style={{ color: ok ? C.primary : warn ? C.secondary : C.error, fontWeight: "bold" }}>{ratio}:1 {ok ? "[صحي]" : warn ? "[ضعيف]" : "[خسارة]"}</span>
            </div>
          </div>
        );
      })}
    </Pane>
  );
}

function DashboardViz({ data }) {
  const d = data;
  const Metric = ({ label, value, color }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 0, fontFamily: FONT_AR }}>{">"} {label}</div>
      <div className="founder-metric-val" style={{ fontSize: 24, color, textShadow: GLOW(color, 6), fontWeight: "bold", lineHeight: 1.1, fontFamily: FONT_AR }}>{value}</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontFamily: FONT, fontSize: 12, color: C.secondary, textShadow: GLOW(C.secondary, 4), marginBottom: 12, letterSpacing: 0 }}>
        $ present --to=board --mode=confidence
      </div>
      <div className="founder-dashboard" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        <Pane title="محرك الإيرادات" status="OK">
          <Metric label="ARR" value={d.arr} color={C.primary} />
          <Metric label="MRR" value={d.mrr} color={C.primary} />
          <Metric label="صافي الاحتفاظ بالإيرادات NDR" value={d.ndr} color={C.cyan} />
        </Pane>
        <Pane title="اقتصاديات الوحدة" status="OK" accent={C.secondary}>
          <Metric label="LTV" value={d.ltv} color={C.primary} />
          <Metric label="CAC" value={d.cac} color={C.secondary} />
          <Metric label="نسبة LTV:CAC" value={d.ratio} color={C.primary} />
        </Pane>
        <Pane title="صحة النمو" status="OK" accent={C.cyan}>
          <Metric label="التسرب الشهري" value={d.churn} color={C.primary} />
          <Metric label="معدل التفعيل" value={d.activation} color={C.cyan} />
          <Metric label="إجمالي الزبائن" value={d.customers.toLocaleString()} color={C.primary} />
          <Metric label="معدل الحرق" value={d.burn} color={C.secondary} />
          <Metric label="Runway" value={d.runway} color={C.secondary} />
        </Pane>
      </div>
      <div style={{ fontFamily: FONT_AR, fontSize: 14, color: C.primary, textShadow: GLOW(C.primary, 4), marginTop: 16, padding: "12px 14px", border: `1px dashed ${C.primary}`, lineHeight: 1.9 }}>
        {">"} الحكم: احتفاظ قوي (تسرب 3.1%)، اقتصاديات وحدة صحية<br/>
        {">"} (LTV:CAC 4.3:1)، وARR 240K ينمو ~10% شهرياً.<br/>
        {">"} هذه آلة تستحق التوسع. السلسلة A: جاهز.
      </div>
    </div>
  );
}

function VizRouter({ viz }) {
  if (!viz) return null;
  const map = { runway: RunwayViz, funnel: FunnelViz, churn: ChurnViz, mrr: MrrViz, ltvcac: LtvCacViz, dashboard: DashboardViz };
  const Comp = map[viz.type];
  return Comp ? <Comp {...viz} /> : null;
}

// ═══════════════════════════════════════════════════
// ─── التطبيق الرئيسي ───
// ═══════════════════════════════════════════════════

const PHASES = { INTRO: -1, NARRATIVE: 0, LESSON: 1, QUIZ: 2, RESULT: 3, VIZ: 4 };

export default function FounderSH() {
  const [chIdx, setChIdx] = useState(0);
  const [phase, setPhase] = useState(PHASES.INTRO);
  const [selOpt, setSelOpt] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);
  const [narrDone, setNarrDone] = useState(false);
  const mainRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const ch = CHAPTERS[chIdx];
  const isLast = chIdx === CHAPTERS.length - 1;

  const scroll = () => setTimeout(() => { if (mainRef.current) mainRef.current.scrollTop = mainRef.current.scrollHeight; }, 120);

  const advance = useCallback(() => {
    if (phase === PHASES.INTRO) { setPhase(PHASES.NARRATIVE); setNarrDone(false); scroll(); }
    else if (phase === PHASES.NARRATIVE) { setPhase(PHASES.LESSON); scroll(); }
    else if (phase === PHASES.LESSON) { setPhase(ch.lesson.scenario ? PHASES.QUIZ : PHASES.VIZ); scroll(); }
    else if (phase === PHASES.RESULT) { setPhase(PHASES.VIZ); scroll(); }
    else if (phase === PHASES.VIZ && !isLast) {
      setChIdx(i => i + 1); setPhase(PHASES.INTRO); setSelOpt(null); setNarrDone(false);
      if (mainRef.current) mainRef.current.scrollTop = 0;
    }
  }, [phase, ch, isLast]);

  const handleAnswer = (opt, idx) => {
    setSelOpt(idx); setTotal(t => t + 1);
    if (opt.correct) setScore(s => s + 1);
    setPhase(PHASES.RESULT); scroll();
  };

  const reset = () => { setChIdx(0); setPhase(PHASES.INTRO); setSelOpt(null); setScore(0); setTotal(0); setNarrDone(false); };

  const btnLabels = {
    [PHASES.INTRO]: "[ ابدأ الفصل ]",
    [PHASES.NARRATIVE]: "[ تابع ← الدرس ]",
    [PHASES.LESSON]: ch.lesson.scenario ? "[ تابع ← التحدي ]" : "[ تابع ← البيانات ]",
    [PHASES.RESULT]: "[ شوف البيانات ]",
    [PHASES.VIZ]: !isLast ? "[ الفصل التالي ← ]" : null,
  };
  const btnText = btnLabels[phase] || null;

  const Btn = ({ onClick, children, color = C.primary }) => (
    <button onClick={onClick} className="founder-btn" style={{
      fontFamily: FONT_AR, fontSize: 13, padding: "10px 20px", cursor: "pointer",
      background: "transparent", color, border: `1px solid ${color}`,
      textShadow: GLOW(color, 3), letterSpacing: 0, transition: "all 0.15s",
      display: "block", width: isMobile ? "100%" : "auto",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = C.bg; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = color; }}
    >{children}</button>
  );

  const actColor = ch.act === "I" ? C.primary : ch.act === "II" ? C.secondary : C.cyan;

  return (
    <div dir="rtl" style={{ background: C.bg, minHeight: "100vh", color: C.primary, fontFamily: FONT_AR, display: "flex", flexDirection: "column", overflowX: "hidden", maxWidth: "100vw" }}>
      <Scanlines />

      {/* الشريط العلوي */}
      <div className="founder-header" style={{ borderBottom: `1px solid ${C.border}`, padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, flexShrink: 0 }}>
        <div style={{ fontSize: 13, letterSpacing: 0 }}>
          <span style={{ color: C.secondary }}>$</span> FOUNDER.SH <span style={{ color: C.muted, fontSize: 10 }}>v2.0 - الاكاديمية</span>
        </div>
        <div style={{ fontSize: 11, color: C.muted, display: "flex", gap: 16 }}>
          <span>فصل {chIdx + 1}/{CHAPTERS.length}</span>
          <span>النتيجة: <span style={{ color: C.primary }}>{score}/{total}</span></span>
        </div>
      </div>

      {/* الفصل والتنقل */}
      <div className="founder-nav" style={{ borderBottom: `1px solid ${C.border}`, padding: "6px 16px", display: "flex", gap: 6, overflowX: "auto", flexShrink: 0, alignItems: "center", WebkitOverflowScrolling: "touch" }}>
        <span style={{ fontSize: 12, color: actColor, fontWeight: "bold", letterSpacing: 0, marginLeft: 8, whiteSpace: "nowrap", fontFamily: FONT_AR }}>
          الفصل {ch.act}: {ch.actTitle}
        </span>
        {CHAPTERS.map((c, i) => {
          const active = i === chIdx, past = i < chIdx;
          const sameAct = c.act === ch.act;
          return (
            <button key={c.id} onClick={() => { if (i <= chIdx) { setChIdx(i); setPhase(PHASES.INTRO); setSelOpt(null); setNarrDone(false); } }}
              style={{
                fontFamily: FONT, fontSize: 10, padding: "3px 8px",
                cursor: i <= chIdx ? "pointer" : "default",
                background: active ? actColor : "transparent",
                color: active ? C.bg : past ? C.primary : C.muted,
                border: `1px solid ${active ? actColor : C.border}`,
                whiteSpace: "nowrap", opacity: i > chIdx ? 0.3 : sameAct ? 1 : 0.6,
                letterSpacing: 0,
              }}>
              {past ? "✓ " : ""}{c.id.toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* المحتوى الرئيسي */}
      <div ref={mainRef} className="founder-main" style={{ flex: 1, overflowY: "auto", padding: "20px 16px", maxWidth: 880, width: "100%", margin: "0 auto" }}>

        {/* رأس الفصل */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: actColor, letterSpacing: 0, marginBottom: 4 }}>
            الفصل {ch.act} - {ch.actTitle} - الحلقة {chIdx + 1}
          </div>
          <div className="founder-title" style={{ fontSize: 24, color: C.primary, textShadow: GLOW(C.primary, 8), fontWeight: "bold", letterSpacing: 0, fontFamily: FONT_AR }}>
            {ch.title}
          </div>
          <div className="founder-subtitle" style={{ fontSize: 15, color: C.secondary, textShadow: GLOW(C.secondary, 3), marginTop: 4, fontFamily: FONT_AR }}>
            {ch.subtitle}
          </div>
        </div>

        {/* الإحاطة الدرامية */}
        <div style={{ marginBottom: 20, border: `1px solid ${actColor}88`, background: `${actColor}06` }}>
          <div style={{ padding: "5px 14px", background: `${actColor}22`, fontSize: 11, color: actColor, letterSpacing: 0, fontFamily: FONT }}>
            إحاطة الموقف
          </div>
          <div className="founder-intro" style={{ padding: "14px 14px", overflowX: "hidden" }}>
            {ch.terminalIntro.filter(l => l !== "").map((line, i) => (
              <div key={i} style={{
                fontFamily: FONT_AR, fontSize: 14, lineHeight: 1.9, color: actColor,
                textShadow: GLOW(actColor, 2),
                borderRight: line.startsWith(">>") ? `3px solid ${actColor}` : "none",
                paddingRight: line.startsWith(">>") ? 10 : 0,
                marginBottom: 2,
                fontWeight: line.startsWith(">>") ? "bold" : "normal",
              }}>{line}</div>
            ))}
          </div>
        </div>

        {phase === PHASES.INTRO && (
          <div style={{ marginBottom: 20 }}><Btn onClick={advance} color={actColor}>{btnText}</Btn></div>
        )}

        {/* السرد */}
        {phase >= PHASES.NARRATIVE && (
          <div style={{ marginBottom: 20, padding: "16px", border: `1px solid ${C.border}`, background: C.panel, overflowX: "hidden", wordBreak: "break-word" }}>
            <TypedLines lines={ch.narrative} speed={16} lineDelay={70} onDone={() => setNarrDone(true)} />
          </div>
        )}

        {phase === PHASES.NARRATIVE && narrDone && (
          <div style={{ marginBottom: 20 }}><Btn onClick={advance}>{btnText}</Btn></div>
        )}

        {/* الدرس */}
        {phase >= PHASES.LESSON && (
          <div style={{ marginBottom: 20 }}>
            <Pane title={`الدرس: ${ch.lesson.title}`} accent={C.cyan} status="STUDY">
              {/* لماذا يهم */}
              <div style={{ marginBottom: 16, padding: "10px 12px", border: `1px dashed ${C.cyan}44`, background: `${C.cyan}06` }}>
                <div style={{ fontSize: 10, color: C.cyan, letterSpacing: 0, marginBottom: 4 }}>لماذا يهم هذا؟</div>
                <div className="founder-why" style={{ fontSize: 15, color: C.primary, lineHeight: 1.9, fontFamily: FONT_AR }}>{ch.lesson.why}</div>
              </div>
              {ch.lesson.metrics.map((m, i) => (
                <div key={i} style={{ marginBottom: 16, paddingBottom: 14, borderBottom: i < ch.lesson.metrics.length - 1 ? `1px dashed ${C.border}` : "none" }}>
                  <div style={{ fontSize: 14, color: C.secondary, fontWeight: "bold", marginBottom: 4, textShadow: GLOW(C.secondary, 3) }}>{m.name}</div>
                  <div className="founder-def" style={{ fontSize: 15, color: C.primary, lineHeight: 1.9, marginBottom: 8, fontFamily: FONT_AR }}>{m.def}</div>
                  <div style={{ fontSize: 12, color: C.cyan, padding: "6px 10px", background: `${C.cyan}08`, border: `1px solid ${C.cyan}22` }}>
                    <span style={{ color: C.muted }}>المعادلة:</span> {m.formula}
                  </div>
                </div>
              ))}
              {ch.lesson.metrics.length === 0 && (
                <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.9, fontFamily: FONT_AR }}>
                  تعلمت كل مقياس أساسي. الحين شوفهم كنظام واحد متصل - القصة اللي تحكيها ستارتابك من خلال أرقامها.
                </div>
              )}
            </Pane>
            {phase === PHASES.LESSON && (
              <div style={{ marginTop: 16 }}><Btn onClick={advance}>{btnText}</Btn></div>
            )}
          </div>
        )}

        {/* التحدي */}
        {phase >= PHASES.QUIZ && ch.lesson.scenario && (
          <div style={{ marginBottom: 20 }}>
            <Pane title="التحدي" accent={C.secondary} status="ACTIVE">
              <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.9, marginBottom: 14, fontFamily: FONT_AR }}>{ch.lesson.scenario.intro}</div>
              <div style={{ fontSize: 16, color: C.secondary, fontWeight: "bold", marginBottom: 14, padding: "10px 12px", border: `1px dashed ${C.secondary}`, textShadow: GLOW(C.secondary, 3), fontFamily: FONT_AR }}>
                <span style={{ color: C.muted }}>{">"}</span> {ch.lesson.scenario.question}
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {ch.lesson.scenario.options.map((opt, i) => {
                  const chosen = selOpt === i, revealed = phase >= PHASES.RESULT, correct = opt.correct;
                  let bg = "transparent", bc = C.border, tc = C.primary;
                  if (revealed && chosen && correct) { bg = `${C.primary}15`; bc = C.primary; }
                  else if (revealed && chosen && !correct) { bg = `${C.error}15`; bc = C.error; tc = C.error; }
                  else if (revealed && correct) { bg = `${C.primary}08`; bc = C.primary; }
                  return (
                    <button key={i} onClick={() => phase === PHASES.QUIZ && handleAnswer(opt, i)} disabled={phase !== PHASES.QUIZ}
                      className="founder-option"
                      style={{
                        fontFamily: FONT_AR, fontSize: 15, textAlign: "right", padding: "12px 16px",
                        background: bg, color: tc, border: `1px solid ${bc}`,
                        cursor: phase === PHASES.QUIZ ? "pointer" : "default",
                        textShadow: chosen ? GLOW(tc, 3) : "none", transition: "all 0.15s", lineHeight: 1.5,
                      }}
                      onMouseEnter={e => { if (phase === PHASES.QUIZ) { e.target.style.borderColor = C.primary; e.target.style.textShadow = GLOW(C.primary, 3); } }}
                      onMouseLeave={e => { if (phase === PHASES.QUIZ) { e.target.style.borderColor = C.border; e.target.style.textShadow = "none"; } }}
                    ><span style={{ color: C.muted }}>[{String.fromCharCode(65 + i)}]</span> {opt.label}</button>
                  );
                })}
              </div>
            </Pane>
          </div>
        )}

        {/* الرد على الإجابة */}
        {phase >= PHASES.RESULT && ch.lesson.scenario && (
          <div style={{ marginBottom: 20 }}>
            <Pane title={ch.lesson.scenario.options[selOpt]?.correct ? "إجابة صحيحة ✓" : "إجابة خاطئة ✗"}
              accent={ch.lesson.scenario.options[selOpt]?.correct ? C.primary : C.error}
              status={ch.lesson.scenario.options[selOpt]?.correct ? "OK" : "ERR"}>
              <div style={{ fontSize: 15, color: C.primary, lineHeight: 1.9, fontFamily: FONT_AR }}>{ch.lesson.scenario.options[selOpt]?.feedback}</div>
            </Pane>
            {phase === PHASES.RESULT && (
              <div style={{ marginTop: 16 }}><Btn onClick={advance}>{btnText}</Btn></div>
            )}
          </div>
        )}

        {/* المخطط البياني */}
        {phase >= PHASES.VIZ && (
          <div style={{ marginBottom: 20 }}>
            <VizRouter viz={ch.lesson.viz} />

            {/* الأخطاء الشائعة */}
            {ch.pitfalls && ch.pitfalls.length > 0 && (
              <div style={{ marginTop: 16, border: `1px solid ${C.error}44`, background: `${C.error}06` }}>
                <div style={{ padding: "5px 12px", background: `${C.error}18`, fontSize: 10, color: C.error, letterSpacing: 0, fontFamily: FONT_AR, display: "flex", justifyContent: "space-between" }}>
                  <span>الأخطاء الشائعة</span>
                  <span style={{ opacity: 0.6 }}>تجنبها</span>
                </div>
                <div style={{ padding: "12px 14px" }}>
                  {ch.pitfalls.map((p, i) => (
                    <div key={i} style={{ marginBottom: i < ch.pitfalls.length - 1 ? 14 : 0, paddingBottom: i < ch.pitfalls.length - 1 ? 14 : 0, borderBottom: i < ch.pitfalls.length - 1 ? `1px dashed ${C.error}33` : "none" }}>
                      <div style={{ fontSize: 14, color: C.error, fontFamily: FONT_AR, marginBottom: 4, lineHeight: 1.8 }}>
                        <span style={{ color: C.muted }}>✗</span> {p.mistake}
                      </div>
                      <div style={{ fontSize: 14, color: C.primary, fontFamily: FONT_AR, lineHeight: 1.8, paddingRight: 14 }}>
                        <span style={{ color: C.primary, opacity: 0.5 }}>→</span> {p.fix}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* الدرس الرئيسي */}
            {ch.takeaway && (
              <div style={{ marginTop: 16, padding: "12px 14px", border: `1px solid ${C.secondary}`, background: `${C.secondary}0a`, display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ fontFamily: FONT_AR, fontSize: 12, color: C.bg, background: C.secondary, padding: "2px 8px", fontWeight: "bold", letterSpacing: 0, flexShrink: 0, textShadow: "none" }}>الدرس الرئيسي</div>
                <div className="founder-takeaway" style={{ fontFamily: FONT_AR, fontSize: 15, color: C.secondary, textShadow: GLOW(C.secondary, 3), lineHeight: 1.9 }}>{ch.takeaway}</div>
              </div>
            )}
            {btnText && <div style={{ marginTop: 16 }}><Btn onClick={advance} color={C.secondary}>{btnText}</Btn></div>}
            {isLast && phase === PHASES.VIZ && (
              <div style={{ marginTop: 24, textAlign: "center" }}>
                <div style={{ fontSize: 14, color: C.cyan, textShadow: GLOW(C.cyan, 8), marginBottom: 8, letterSpacing: 0 }}>
                  ═══════ اكتمل الكورس ═══════
                </div>
                <div style={{ fontSize: 12, color: C.secondary, marginBottom: 6 }}>
                  النتيجة النهائية: {score}/{total}
                </div>
                <div style={{ fontSize: 15, color: C.primary, marginBottom: 16, lineHeight: 1.9, maxWidth: 500, margin: "0 auto 16px", fontFamily: FONT_AR }}>
                  تعلمت المقاييس اللي تهم. بس تذكر: الأرقام هي اللغة - القصة هي اللي تبنيها أنت. روح وابنِ شيء يستاهل يُقاس.
                </div>
                <button onClick={reset} style={{ fontFamily: FONT_AR, fontSize: 13, padding: "10px 24px", cursor: "pointer", background: C.cyan, color: C.bg, border: `1px solid ${C.cyan}`, letterSpacing: 0, fontWeight: "bold" }}
                  onMouseEnter={e => { e.target.style.background = C.primary; e.target.style.borderColor = C.primary; }}
                  onMouseLeave={e => { e.target.style.background = C.cyan; e.target.style.borderColor = C.cyan; }}
                >[ ابدأ من الفصل الأول ]</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* التذييل */}
      <div style={{ borderTop: `1px solid ${C.border}`, padding: "6px 16px", fontSize: 10, color: C.muted, display: "flex", justifyContent: "space-between", flexShrink: 0, flexWrap: "wrap", gap: 4 }}>
        <span>$ founder.sh --mode=academy --v2.0</span>
        <span>الأرقام تحكي قصصاً. ابنِ قصتك.</span>
      </div>
    </div>
  );
}
